import { createGuid } from '@bimser/core';
import IconAdd from '@bimser/icons/16/add';
import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import BCButton from '../../BCButton';
import BCEmpty from '../../BCEmpty';
import { findMessage } from '../../BCIntl';
import { Helpers, RuleEngineEntities } from '../../BCRuleManager';
import BCScrollbars from '../../BCScrollbars';
import Styles from '../assets/styles.scss';
import { IRuleManager } from '../entities';
import { RuleItem } from './RuleItem';

const scrollbarStyles = { width: '100%', height: 'calc(100% - 50px)' };

const RuleManager = React.memo((props: IRuleManager) => {
	const prevRulesRef = React.useRef(props.rules);

	React.useEffect(() => {
		if (!prevRulesRef.current.equals(props.rules)) {
			props.onDataChanged?.(props.rules);
			prevRulesRef.current = props.rules;
		}
	}, [props.rules]);

	const sortedRuleKeys = React.useMemo(
		() =>
			props.rules
				.sort((a, b) => {
					if (a.orderNo < b.orderNo) return -1;
					if (a.orderNo > b.orderNo) return 1;
					return 0;
				})
				.keySeq()
				.toArray(),
		[props.rules]
	);

	const generateNewRuleName = (): string => {
		const allNames: string[] = props.rules
				.map(r => r.name)
				.valueSeq()
				.toArray(),
			newRuleString = findMessage.get('101149') + ' ';
		let keyFound = false,
			index: number = 0;

		while (!keyFound) {
			index++;
			keyFound = allNames.findIndex(n => n === newRuleString + index) === -1;
		}

		return newRuleString + index;
	};

	const createNewRule = () => {
		props.onRuleAdded?.(
			RuleEngineEntities.RuleFactory({
				id: createGuid(),
				name: generateNewRuleName(),
				orderNo: Helpers.generateOrderNo(
					props.rules
						.valueSeq()
						.toArray()
						.map(r => r.orderNo)
				)
			})
		);
	};

	const renderAddButton = (primary?: boolean) => (
		<BCButton
			icon={<IconAdd />}
			text={findMessage.get('101149')}
			onClick={createNewRule}
			type={primary ? 'default' : 'text'}
			disabled={props.disabled}
		/>
	);

	const onRuleClone = (rule: RuleEngineEntities.Rule) => {
		const updatedRule = rule.set(
			'orderNo',
			Helpers.generateOrderNo(
				props.rules
					.valueSeq()
					.toArray()
					.map(r => r.orderNo)
			)
		);
		props.onRuleClone?.(updatedRule);
	};

	const renderEmptyRuleTemplate = () => (
		<BCEmpty description={`${findMessage.get('101150')} ${findMessage.get('101151')}`}>
			{!props.disabled ? renderAddButton(true) : null}
		</BCEmpty>
	);

	const moveItem = (dragIndex: number, hoverIndex: number) => {
		const draggedItemId = sortedRuleKeys[dragIndex];
		const hoveredItemId = sortedRuleKeys[hoverIndex];

		const updatedDraggedItem = props.rules
			.get(draggedItemId)
			.set('orderNo', props.rules.get(hoveredItemId).orderNo);
		const updatedHoveredItem = props.rules
			.get(hoveredItemId)
			.set('orderNo', props.rules.get(draggedItemId).orderNo);

		props.onRuleUpdated(updatedDraggedItem, true);
		props.onRuleUpdated(updatedHoveredItem, true);
	};

	const renderRuleItem = (rule: RuleEngineEntities.Rule, index: number) => {
		return (
			<RuleItem
				key={rule.id}
				id={rule.id}
				index={index}
				rule={rule}
				moveCard={moveItem}
				onRuleChange={props.onRuleChange}
				onRuleClone={onRuleClone}
				onRuleDeleted={props.onRuleDeleted}
				onRuleUpdated={props.onRuleUpdated}
			/>
		);
	};

	const renderRules = () => {
		if (!props.rules?.count()) {
			return renderEmptyRuleTemplate();
		}

		const ruleArr: JSX.Element[] = [];

		sortedRuleKeys.forEach((key, index) => {
			const rule = props.rules.get(key);
			ruleArr.push(renderRuleItem(rule, index));
		});

		return (
			<div className={Styles.rulesContainer}>
				<DndProvider backend={HTML5Backend}>{ruleArr}</DndProvider>
			</div>
		);
	};

	const renderHeaderAddButton = () => {
		if (props.rules?.count()) {
			return renderAddButton();
		}
	};

	return (
		<>
			<div className={Styles.newRuleButton}>
				<h3>{findMessage.get('101152')}</h3>
				{renderHeaderAddButton()}
			</div>
			<BCScrollbars styles={scrollbarStyles}>{renderRules()}</BCScrollbars>
		</>
	);
});

export default RuleManager;
